import type { DateMenus, Menu } from '../../../../types/groupOrder/dateMenus'
import type { ConceptSummary, ScheduleDay } from './useMenuGeneration'

// --- Time / cutoff formatting ---

export const formatCutoffHours = (hours: number | null): string => {
  if (hours === null) {
    return 'N/A'
  }
  const h = Math.floor(hours)
  const m = Math.round((hours - h) * 60)

  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

export const formatMilitaryTime = (time: number): string => {
  const hour = Math.floor(time / 100)
  const minute = time % 100
  const period = hour >= 12 ? 'PM' : 'AM'
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour

  return `${displayHour}:${String(minute).padStart(2, '0')} ${period}`
}

export const formatCutoff = (concept: ConceptSummary): string => {
  const days = concept.groupOrderCutoffDays
  const time = concept.groupOrderCutoffTime
  const includesWeekends = concept.groupOrderCutoffIncludesWeekends

  if (days == null || days === 0) {
    const timeStr = time != null ? formatMilitaryTime(time) : ''

    return timeStr ? `Same day ${timeStr}` : 'Same day'
  }

  const unit = includesWeekends ? 'day' : 'biz day'
  const plural = days > 1 ? 's' : ''
  const timeStr = time != null ? ` ${formatMilitaryTime(time)}` : ''

  return `${days} ${unit}${plural} before${timeStr}`
}

// --- Chef name truncation ---

export const truncateChefs = (names: string[], max = 2): string => {
  if (names.length === 0) {
    return ''
  }
  if (names.length <= max) {
    return names.join(', ')
  }

  return `${names.slice(0, max).join(', ')} +${names.length - max} more`
}

// --- Calendar data ---

export interface MonthData {
  year: number
  month: number // 0-indexed
  label: string
  weeks: (DayCell | null)[][] // 7-column grid
}

export interface DayCell {
  date: string // YYYY-MM-DD
  dayOfMonth: number
  menuCount: number
  chefNames: string[]
  dropoff: string // ISO datetime
  cutoff: string // ISO datetime
}

export const buildCalendarData = (
  previews: DateMenus[],
  includeWeekends: boolean,
): MonthData[] => {
  // Build a map from dateStr -> preview info
  const dateMap = new Map<
    string,
    { menuCount: number; chefNames: string[]; dropoff: string; cutoff: string }
  >()
  for (const dm of previews) {
    const chefNames = dm.menus.map((m: Menu) => m.chef.name)
    dateMap.set(dm.dateStr, {
      menuCount: dm.menus.length,
      chefNames,
      dropoff: dm.date,
      cutoff: dm.cutoffTime,
    })
  }

  // Find min/max dates
  const dateStrs = previews.map((p) => p.dateStr).sort()
  const minDate = new Date(dateStrs[0] + 'T00:00:00')
  const maxDate = new Date(dateStrs[dateStrs.length - 1] + 'T00:00:00')

  const cols = includeWeekends ? 7 : 5
  const months: MonthData[] = []
  let current = new Date(minDate.getFullYear(), minDate.getMonth(), 1)

  while (
    current.getFullYear() < maxDate.getFullYear() ||
    (current.getFullYear() === maxDate.getFullYear() &&
      current.getMonth() <= maxDate.getMonth())
  ) {
    const year = current.getFullYear()
    const month = current.getMonth()
    const label = current.toLocaleString('default', {
      month: 'long',
      year: 'numeric',
    })

    // Build weeks grid
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    // Monday = 0, Sunday = 6
    const startDow = (firstDay.getDay() + 6) % 7
    // If weekday-only and 1st falls on a weekend, first visible day is Monday (no pad)
    const startPad = includeWeekends ? startDow : startDow >= 5 ? 0 : startDow

    const weeks: (DayCell | null)[][] = []
    let week: (DayCell | null)[] = new Array(startPad).fill(null)

    for (let d = 1; d <= lastDay.getDate(); d++) {
      const dow = (new Date(year, month, d).getDay() + 6) % 7
      if (!includeWeekends && dow >= 5) {
        continue
      }

      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(
        d,
      ).padStart(2, '0')}`
      const info = dateMap.get(dateStr)
      week.push(
        info
          ? {
              date: dateStr,
              dayOfMonth: d,
              menuCount: info.menuCount,
              chefNames: info.chefNames,
              dropoff: info.dropoff,
              cutoff: info.cutoff,
            }
          : {
              date: dateStr,
              dayOfMonth: d,
              menuCount: 0,
              chefNames: [],
              dropoff: '',
              cutoff: '',
            },
      )

      if (week.length === cols) {
        weeks.push(week)
        week = []
      }
    }

    if (week.length > 0) {
      while (week.length < cols) {
        week.push(null)
      }
      weeks.push(week)
    }

    months.push({ year, month, label, weeks })
    current = new Date(year, month + 1, 1)
  }

  return months
}

// --- SSE parsing ---

export interface SSEEvent {
  type: string
  data: any
}

export const parseSSEEvents = (
  buffer: string,
): { events: SSEEvent[]; remainder: string } => {
  const parts = buffer.split('\n\n')
  const remainder = parts.pop() || '' // Keep incomplete event in buffer
  const events: SSEEvent[] = []

  for (const part of parts) {
    if (!part.trim() || part.startsWith(':')) {
      continue
    } // Skip heartbeats

    let eventType = ''
    let eventData = ''
    for (const line of part.split('\n')) {
      if (line.startsWith('event: ')) {
        eventType = line.slice(7)
      } else if (line.startsWith('data: ')) {
        eventData = line.slice(6)
      }
    }

    if (!eventType || !eventData) {
      continue
    }

    events.push({ type: eventType, data: JSON.parse(eventData) })
  }

  return { events, remainder }
}

// --- Schedule helpers ---

export const extractMenuIds = (schedule: ScheduleDay[]): string[] => {
  const ids = new Set<string>()
  for (const day of schedule) {
    for (const menu of day.menus) {
      ids.add(menu.menu_id)
    }
  }

  return Array.from(ids)
}

export interface ScheduleItem {
  date: string
  menuIds: string[]
  active: boolean
}

export const buildSchedulePayload = (
  schedule: ScheduleDay[],
  activeMap?: Record<string, boolean>,
): ScheduleItem[] => {
  if (activeMap) {
    return schedule
      .filter((day) => day.date in activeMap)
      .map((day) => ({
        date: day.date,
        menuIds: day.menus.map((m) => m.menu_id),
        active: !!activeMap[day.date],
      }))
  }

  return schedule.map((day) => ({
    date: day.date,
    menuIds: day.menus.map((m) => m.menu_id),
    active: false,
  }))
}
